import React, { memo, useEffect, useState } from "react"
import PropTypes from "prop-types"
import { useDispatch } from "react-redux"
import { Card, Radio, Typography, Space, Image, Flex } from "antd"
import { detachPaymentMethod, getPaymentMethods, setSelectedCardLast4, setSelectedPaymentMethodId } from "store/user"
import { brandImage } from "components/bookings/helpers"
import styles from "./BookingForm.module.css"
import IconWithBg from "ui/IconWithBg"
import { DeleteOutlined, EditOutlined } from "@ant-design/icons"
import DeleteCardModal from "pagesV2/client/Profile/Tabs/PaymentDetails/deleteModal"
import { toaster } from "components/common/Toast"
import EditCardExpiryDate from "./modals/EditCardExpiryDate"

const { Text } = Typography

const PaymentMethodList = ({ active, paymentMethods, className, setActive, existingClientId, clientId, guideId }) => {
  const dispatch = useDispatch()
  const hasPaymentMethods = !!paymentMethods?.length
  const [isDeleteCardModalVisible, setIsDeleteCardModalVisible] = useState(false)
  const [selectedCard, setSelectedCard] = useState(null)
  const [isEditOpenModal, setIsEditOpenModal] = useState(false)
  // const userData = useSelector((state) => state?.user)
  useEffect(() => {
    if (active && paymentMethods?.length) {
      dispatch(setSelectedPaymentMethodId(active))
      const selectedCard = paymentMethods?.find((card) => card.payment_method_id === active)
      if (selectedCard) {
        dispatch(setSelectedCardLast4(selectedCard.last4))
      }
    }
  }, [active, paymentMethods, dispatch])

  const handleCardChange = (e) => {
    dispatch(setSelectedPaymentMethodId(e.target?.value))
    setActive(e.target?.value)
    const selectedCard = paymentMethods?.find((card) => card?.payment_method_id === e.target?.value)
    if (selectedCard) {
      dispatch(setSelectedCardLast4(selectedCard?.last4))
    }
  }

  const handleDeleteCardClick = (card) => {
    setIsDeleteCardModalVisible(true)
    setSelectedCard(card)
  }

  const handleDeleteCardConfirm = () => {
    const isDeletingSelected = selectedCard?.payment_method_id === active
    dispatch(detachPaymentMethod(selectedCard?.id)).then((response) => {
      if (isDeletingSelected) {
        dispatch(setSelectedPaymentMethodId(null))
      }
      if (existingClientId) {
        dispatch(getPaymentMethods(existingClientId, guideId))
      }
      if (clientId) {
        dispatch(getPaymentMethods(clientId, guideId))
      }
      setIsDeleteCardModalVisible(false)
      setSelectedCard(null)
      toaster.success(response?.message)
    })
  }

  const handleEditCardClick = (card) => {
    setSelectedCard(card)
    setIsEditOpenModal(true)
  }

  const handleEditCardModalClose = () => {
    setIsEditOpenModal(false)
    setSelectedCard(null)
  }

  return (
    <div className={`${styles.container} ${className}`}>
      <Radio.Group value={active} onChange={handleCardChange} className={styles.radioGroup}>
        <Space direction="vertical" className={styles.spaceContainer}>
          {hasPaymentMethods &&
            paymentMethods?.map((card) => (
              <Card key={card?.payment_method_id} className={active === card?.payment_method_id ? styles.cardActive : styles.card}>
                <div className={styles.cardContent}>
                  <Space align="center">
                    <Radio value={card?.payment_method_id} />
                    <Image src={brandImage(card?.brand)} className={styles.cardImage} preview={false} alt={card?.brand} />
                    <div className={styles.cardInfo}>
                      <div className={styles.cardDetails}>
                        <Text strong className={styles.brandName}>
                          {card?.brand.charAt(0).toUpperCase() + card?.brand.slice(1)}
                        </Text>
                        <Text className={styles.cardNumber}>****{card?.last4}</Text>
                        <Text className={styles.expiryDate}>
                          Expires {card?.exp_month.toString().padStart(2, "0")}/{card?.exp_year.toString().slice(-2)}
                        </Text>
                      </div>
                    </div>
                  </Space>
                  <Flex gap={8}>
                    <IconWithBg icon={<EditOutlined className={styles.editIcon} />} onClick={() => handleEditCardClick(card)} />
                    <IconWithBg icon={<DeleteOutlined className={styles.deleteIcon} />} onClick={() => handleDeleteCardClick(card)} />
                  </Flex>
                </div>
              </Card>
            ))}
        </Space>
      </Radio.Group>
      {isDeleteCardModalVisible && (
        <DeleteCardModal
          open={isDeleteCardModalVisible}
          onCancel={() => setIsDeleteCardModalVisible(false)}
          onConfirm={handleDeleteCardConfirm}
          cardLast4={selectedCard?.last4}
        />
      )}
      {isEditOpenModal && selectedCard && (
        <EditCardExpiryDate isOpen={isEditOpenModal} onClose={handleEditCardModalClose} selectedCard={selectedCard} />
      )}
    </div>
  )
}

PaymentMethodList.propTypes = {
  active: PropTypes.string.isRequired,
  paymentMethods: PropTypes.array,
  className: PropTypes.string,
  setActive: PropTypes.func,
  existingClientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  clientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  guideId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
}

PaymentMethodList.defaultProps = {
  paymentMethods: [],
  className: "",
  existingClientId: undefined
}

export default memo(PaymentMethodList)
